/**
 * NotreSystemePage - Page marketing "Notre Système"
 * Explique le modèle d'affaires et rassure les clients
 * Charge le contenu depuis la base de données (éditable via admin)
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { 
  Search, 
  Package, 
  Plane, 
  Shield, 
  Camera, 
  MessageCircle,
  ChevronRight,
  Quote,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images par défaut (fallback)
const DEFAULT_IMAGES = {
  hero: 'https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&w=1920',
  warehouse: 'https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg?auto=compress&cs=tinysrgb&w=1200',
  qualityControl: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800',
  packaging: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800',
  shipping: 'https://images.pexels.com/photos/36652827/pexels-photo-36652827.jpeg?auto=compress&cs=tinysrgb&w=1920',
};

// Icônes pour les étapes
const STEP_ICONS = {
  search: Search,
  package: Package,
  plane: Plane,
};

// Icônes pour les garanties
const GUARANTEE_ICONS = {
  shield: Shield,
  camera: Camera,
  message: MessageCircle,
};

export function NotreSystemePage() {
  const { t, i18n } = useTranslation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRTL = i18n.dir() === 'rtl';
  const lang = i18n.language;

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing-pages/notre-systeme`);
        setPageData(res.data);
      } catch (err) {
        console.error('Error loading page:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  // Helper pour obtenir le texte localisé
  const getText = (obj, fallback = '') => {
    if (!obj) return fallback;
    return obj[lang] || obj.fr || obj.en || fallback;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#1A1A1A]">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C0000C]" />
        </main>
        <Footer />
      </div>
    );
  }

  // Utiliser les données de la DB ou les valeurs par défaut
  const sections = pageData?.sections || {};
  const heroImage = sections.hero?.image || DEFAULT_IMAGES.hero;
  const warehouseImage = sections.warehouse_image || DEFAULT_IMAGES.warehouse;
  const qualityImage = sections.quality_control?.image || DEFAULT_IMAGES.qualityControl;
  const pricingImage = sections.pricing?.image || DEFAULT_IMAGES.packaging;
  const shippingImage = sections.shipping_image || DEFAULT_IMAGES.shipping;

  return (
    <div className="min-h-screen flex flex-col bg-[#1A1A1A]">
      <Header />
      
      <main className="flex-1">
        {/* HERO SECTION */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#1A1A1A]" />
          
          {/* Content avec ombres */}
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ 
                textShadow: '0 4px 8px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.9), 0 8px 16px rgba(0,0,0,0.5)' 
              }}
            >
              {getText(sections.hero?.title, 'Notre Système — La Transparence au Cœur')}
            </h1>
            <p 
              className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto"
              style={{ 
                textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.5)' 
              }}
            >
              {getText(sections.hero?.subtitle, 'Nous avons repensé la mode pour vous offrir le meilleur rapport qualité-prix possible.')}
            </p>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="w-8 h-8 text-white/50 rotate-90" />
          </div>
        </section>

        {/* INTRODUCTION */}
        <section className="py-16 md:py-24 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {getText(sections.intro?.text, 'Chez Mutagim.shop, nous avons fait le choix de la transparence totale.')}
            </p>
          </div>
        </section>

        {/* 3 ÉTAPES */}
        <section className="py-16 md:py-24 px-6 bg-zinc-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              {t('notreSystème.howItWorks', 'Comment ça marche ?')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {(sections.steps || []).map((step, index) => {
                const IconComponent = STEP_ICONS[step.icon] || Search;
                return (
                  <div key={index} className="text-center group">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#C0000C]/10 border border-[#C0000C]/30 flex items-center justify-center group-hover:bg-[#C0000C]/20 transition-colors">
                      <IconComponent className="w-10 h-10 text-[#C0000C]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      {index + 1}. {getText(step.title)}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {getText(step.text)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* IMAGE PLEINE LARGEUR - ENTREPÔT */}
        <section className="relative h-[50vh] md:h-[60vh]">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${warehouseImage})` }}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-black/40 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/50 to-transparent h-1/4" />
          
          {/* Title overlay avec ombres */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-6">
              <h2 
                className="text-3xl md:text-5xl lg:text-6xl font-bold text-white"
                style={{ 
                  textShadow: '0 4px 8px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.9), 0 8px 16px rgba(0,0,0,0.5)' 
                }}
              >
                {t('notreSystème.warehouseTitle', 'Nos Entrepôts')}
              </h2>
              <p 
                className="text-lg md:text-xl text-white/90 mt-4 max-w-2xl mx-auto"
                style={{ 
                  textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.5)' 
                }}
              >
                {t('notreSystème.warehouseSubtitle', 'Des milliers de produits prêts à être expédiés')}
              </p>
            </div>
          </div>
        </section>

        {/* IMAGE + TEXTE - CONTRÔLE QUALITÉ */}
        <section className="py-16 md:py-24 px-6">
          <div className={cn(
            "max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center",
            isRTL && "md:flex-row-reverse"
          )}>
            {/* Image avec zoom */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={qualityImage}
                alt="Contrôle qualité"
                className="w-full h-[450px] object-cover scale-110 hover:scale-115 transition-transform duration-500"
              />
            </div>
            
            {/* Text */}
            <div className={isRTL ? "text-right" : ""}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {getText(sections.quality_control?.title, 'Un Contrôle Qualité Sans Compromis').split(' ').slice(0, -2).join(' ')}
                <span className="text-[#C0000C]"> {getText(sections.quality_control?.title, 'Sans Compromis').split(' ').slice(-2).join(' ')}</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                {getText(sections.quality_control?.text)}
              </p>
            </div>
          </div>
        </section>

        {/* STATISTIQUES */}
        <section className="py-16 md:py-24 px-6 bg-zinc-900/50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-3 gap-4 md:gap-8">
              {(sections.stats || []).map((stat, index) => (
                <div key={index} className="text-center p-6 md:p-8 rounded-2xl bg-zinc-800/50 border border-zinc-700">
                  <p className="text-3xl md:text-5xl font-bold text-[#C0000C] mb-2">{stat.value}</p>
                  <p className="text-sm md:text-base text-gray-400">{getText(stat.label)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IMAGE + TEXTE - POURQUOI NOS PRIX */}
        <section className="py-16 md:py-24 px-6">
          <div className={cn(
            "max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center",
            !isRTL && "md:flex-row-reverse"
          )}>
            {/* Text */}
            <div className={isRTL ? "text-right md:order-2" : "md:order-1"}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {getText(sections.pricing?.title, 'Pourquoi Nos Prix Sont Imbattables').split(' ').slice(0, -1).join(' ')}
                <span className="text-[#C0000C]"> {getText(sections.pricing?.title, 'Imbattables').split(' ').slice(-1)}</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                {getText(sections.pricing?.text)}
              </p>
              <ul className="space-y-3 text-gray-400 text-lg">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C0000C] rounded-full" />
                  {t('notreSystème.noPhysicalStore', 'Pas de boutique physique')}
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C0000C] rounded-full" />
                  {t('notreSystème.noEuropeStock', 'Pas de stock en Europe à financer')}
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C0000C] rounded-full" />
                  {t('notreSystème.noDistributors', 'Pas de distributeurs qui prennent leur marge')}
                </li>
              </ul>
            </div>
            
            {/* Image */}
            <div className={cn(
              "rounded-2xl overflow-hidden shadow-2xl",
              !isRTL ? "md:order-2" : "md:order-1"
            )}>
              <img 
                src={pricingImage}
                alt="Emballage soigné"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </section>

        {/* CITATION */}
        <section className="py-16 md:py-24 px-6 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto text-center">
            <Quote className="w-12 h-12 text-[#C0000C]/50 mx-auto mb-6" />
            <blockquote className="text-2xl md:text-3xl text-white font-light italic leading-relaxed mb-6">
              "{getText(sections.quote?.text, 'Nous ne cachons rien. Nos produits viennent de Chine, nos prix le reflètent, et notre qualité vous surprendra.')}"
            </blockquote>
            <p className="text-[#C0000C] font-semibold">— {getText(sections.quote?.author, "L'équipe Mutagim.shop")}</p>
          </div>
        </section>

        {/* IMAGE PLEINE LARGEUR - PORT */}
        <section className="relative h-[40vh] md:h-[50vh]">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${shippingImage})` }}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-black/40 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/50 to-transparent h-1/4" />
          
          {/* Title overlay avec ombres */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-6">
              <h2 
                className="text-3xl md:text-5xl lg:text-6xl font-bold text-white"
                style={{ 
                  textShadow: '0 4px 8px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.9), 0 8px 16px rgba(0,0,0,0.5)' 
                }}
              >
                {t('notreSystème.shippingTitle', 'Livraison Mondiale')}
              </h2>
              <p 
                className="text-lg md:text-xl text-white/90 mt-4 max-w-2xl mx-auto"
                style={{ 
                  textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 4px 8px rgba(0,0,0,0.5)' 
                }}
              >
                {t('notreSystème.shippingSubtitle', 'Directement de nos entrepôts vers chez vous')}
              </p>
            </div>
          </div>
        </section>

        {/* 3 GARANTIES */}
        <section className="py-16 md:py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              {t('notreSystème.guarantees', 'Nos Garanties')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {(sections.guarantees || []).map((guarantee, index) => {
                const IconComponent = GUARANTEE_ICONS[guarantee.icon] || Shield;
                return (
                  <div key={index} className="text-center p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#C0000C]/50 transition-colors">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C0000C]/10 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-[#C0000C]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {getText(guarantee.title)}
                    </h3>
                    <p className="text-gray-400">
                      {getText(guarantee.text)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CALL TO ACTION */}
        <section className="py-20 md:py-28 px-6 bg-gradient-to-b from-[#1A1A1A] to-zinc-900">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {getText(sections.cta?.title, 'Prêt à Découvrir Notre Sélection ?')}
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              {t('notreSystème.ctaSubtitle', 'Explorez notre catalogue et profitez de nos prix imbattables.')}
            </p>
            <Link to="/">
              <Button 
                size="lg" 
                className="bg-[#C0000C] hover:bg-[#A0000A] text-white text-lg px-8 py-6 h-auto"
              >
                {getText(sections.cta?.button, 'Explorer la Boutique')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}

export default NotreSystemePage;
